/* eslint-disable @typescript-eslint/no-unused-vars */

import { Router } from 'vue-router';
import { Store } from 'vuex';

/**
 * The error handler class is a catch all class for all HTTP
 * REST methods, that can and will catch errors and throw them
 * to the right places.
 */
export class ErrorHandler {

	/**
	 * This will initialise a new instance of the error handler class, in which
	 * it can be called to handle requests from the HTTP helper.
	 *
	 * @param store The store instance.
	 * @param router The router instance.
	 */
	public constructor(protected store: Store<any>, protected router: Router) {}

	/**
	 * The dipatch method is a general catch all, and will be called on
	 * every request, in which it will check the status codes.
	 *
	 * @param response The response from the Fetch request.
	 */
	public dispatch(response: Response, url: string, headers?: any, signal?: AbortSignal, body?: any): void {
		if (response.status === 401) {
			this.handleUnauthorised(response, url, headers, signal, body);
		} else if (response.status === 403) {
			this.handleForbidden(response, url, headers, signal, body);
		} else if (response.status === 500) {
			this.handleServerError(response, url, headers, signal, body);
		}
		this.handleServerMessages();
	}

	/**
	 * This method will handle all unauthorised requests (401).
	 *
	 * @param response The response from the Fetch request.
	 */
	protected async handleUnauthorised(response: Response, url: string, headers?: any, signal?: AbortSignal, body?: any): Promise<void> {

		// Validate constraints.
		if ((globalThis as any).isStartup) return;
		if (response.status !== 401) return;
		if (['/auth/login', '/auth/sso/authenticate'].includes(window.location.pathname)) return;

		// Notify the user.
		this.store.dispatch('notification/add', {
			type: 'warning',
			message: 'You have been logged out, this most likely means your session has expired, please re-login to continue.',
		});

		// Supress errors.
		this.store.dispatch('notification/suppress', 2);

		// Reset the account.
		this.store.dispatch('account/logout', 'unauthorised');
		this.router.push('/auth/login');
	}

	/**
	 * This method will handle all forbidden requests (403).
	 *
	 * @param response The response from the Fetch request.
	 */
	protected async handleForbidden(response: Response, url: string, headers?: any, signal?: AbortSignal, body?: any): Promise<void> {

		// Validate constraints.
		if (response.status !== 403) return;

		// Reading the body here consumes it, and the component that made the request often wants to read it too,
		// which then comes back empty. handleServerError already clones for that reason, this one did not, which is
		// why a rejected password reset token showed an empty error banner.
		const result = await response.clone().json().catch(() => ({}));

		if (result?.ip_restriction) {
			this.router.push('/forbidden');
			return;
		}

		// Create a new notification item.
		this.store.dispatch('notification/add', {
			type: 'error',
			message: 'You do not have permission to do that.',
		});
		this.router.push('/');
	}

	/**
	 * This method will handle all internal server errors (500).
	 *
	 * @param response The response from the Fetch request.
	 */
	protected async handleServerError(response: Response, url: string, headers?: any, signal?: AbortSignal, body?: any): Promise<void> {

		// Validate constraints.
		if (response.status !== 500) return;
		const clonedResponse = response.clone();
		const result = await clonedResponse.json().catch(() => ({ error: 'Invalid server response' }));
		let errorMessage = result.error;

		// Process the error name.
		const indexOfColon = result.error.indexOf(':');
		if (indexOfColon > -1) {
			errorMessage = result.error.substring((indexOfColon + 1));
		}

		const title = result.title || 'There was a problem completing that request, reason:';

		// Create a new notification item.
		this.store.dispatch('notification/add', {
			type: 'error',
			message: `${title}\n${errorMessage}`,
		});
	}

	/**
	 * Is thrown when any HTTP method errors from a try catch.
	 *
	 * @param err The error class.
	 */
	public handleUncaughtException(err: Error): void {
		this.store.dispatch('notification/add', {
			type: 'error',
			message: `There was a problem completing your request, reason: ${err.message}`,
		});
		console.error(err);
	}

	public handleServerMessages(): void {
		if (this.store.state.configuration.serverWarnings.length > 0) {
			this.store.state.configuration.serverWarnings.forEach((message: string) => {
				const warnings = JSON.parse(window.sessionStorage.getItem('serverWarnings') || '[]');

				const alreadyExist = warnings.filter((warning: string) => warning.startsWith('Disk Usage is currently high'));

				if (alreadyExist.length <= 0 && this.router.currentRoute.value.path !== '/auth/login') {
					this.store.dispatch('notification/add', {
						type: 'warning',
						message,
					});

					window.sessionStorage.setItem('serverWarnings', JSON.stringify([...warnings, message]));
				}
			});
		}
	}
}
